import { useState } from "react";
import { Plus, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

interface SelectableTagsProps {
  options: string[];
  selected: string[];
  onToggle: (item: string) => void;
  placeholder?: string;
  allowCustom?: boolean;
  multiSelect?: boolean;
}

export function SelectableTags({
  options,
  selected,
  onToggle,
  placeholder = "Add custom...",
  allowCustom = false,
  multiSelect = true,
}: SelectableTagsProps) {
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customValue, setCustomValue] = useState("");

  const handleAddCustom = () => {
    const trimmed = customValue.trim();
    if (trimmed && !selected.includes(trimmed) && !options.includes(trimmed)) {
      onToggle(trimmed);
      setCustomValue("");
      setShowCustomInput(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddCustom();
    } else if (e.key === "Escape") {
      setShowCustomInput(false);
      setCustomValue("");
    }
  };

  // Combine options with any custom selected items not in options
  const allItems = [...options, ...selected.filter(s => !options.includes(s))];
  const uniqueItems = [...new Set(allItems)];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {uniqueItems.map((item) => {
          const isSelected = selected.includes(item);
          const isCustom = !options.includes(item);
          
          return (
            <button
              key={item}
              type="button"
              className={`px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-all border flex items-center gap-1.5 ${
                isSelected
                  ? "bg-primary text-primary-foreground border-primary shadow-sm"
                  : "bg-background text-foreground border-border hover:border-primary/50 hover:bg-accent"
              }`}
              onClick={() => {
                if (multiSelect) {
                  onToggle(item);
                } else {
                  // For single select, deselect others first
                  selected.forEach(s => {
                    if (s !== item) onToggle(s);
                  });
                  if (!isSelected) onToggle(item);
                }
              }}
            >
              {item}
              {isCustom && isSelected && (
                <X className="w-3 h-3 ml-1" />
              )}
            </button>
          );
        })}
        
        {allowCustom && !showCustomInput && (
          <button
            type="button"
            onClick={() => setShowCustomInput(true)}
            className="px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-all border border-dashed border-muted-foreground/50 text-muted-foreground hover:border-primary hover:text-primary hover:bg-accent flex items-center gap-1.5"
          >
            <Plus className="w-3.5 h-3.5" />
            Add Other
          </button>
        )}
      </div>
      
      {allowCustom && showCustomInput && (
        <div className="flex gap-2 items-center">
          <Input
            value={customValue}
            onChange={(e) => setCustomValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="h-10 flex-1 text-sm"
            autoFocus
          />
          <Button
            type="button"
            size="sm"
            onClick={handleAddCustom}
            disabled={!customValue.trim()}
            className="h-10 px-4"
          >
            Add
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => {
              setShowCustomInput(false);
              setCustomValue("");
            }}
            className="h-10 px-3"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
